/**
 * Explore API slice.
 * Leaf module (Perf 3.1.b/e Step 5). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';

export const exploreApi = {
  fetchFeed: async (page = 1, filters = {}) => {
    let url = `/json/explore?page=${page}`;
    if (filters.mediaType) url += `&mediaType=${encodeURIComponent(filters.mediaType)}`;
    if (filters.category) url += `&category=${encodeURIComponent(filters.category)}`;
    if (filters.tags) url += `&tags=${encodeURIComponent(filters.tags)}`;
    if (filters.model) url += `&model=${encodeURIComponent(filters.model)}`;
    if (filters.style) url += `&style=${encodeURIComponent(filters.style)}`;
    if (filters.sort) url += `&sort=${encodeURIComponent(filters.sort)}`;
    if (filters.search) url += `&search=${encodeURIComponent(filters.search)}`;
    if (filters.favorites) url += `&favorites=true`;

    const data = await fetchWithAuth(url);
    if (data.error) throw new ApiError(data.message || 'Failed to fetch explore feed', 400, data);
    return { items: data.items, pagination: data.pagination };
  },

  fetchFeatured: async (mediaType = null) => {
    let url = '/json/explore/featured';
    if (mediaType) url += `?mediaType=${encodeURIComponent(mediaType)}`;

    const data = await fetchWithAuth(url);
    if (data.error) throw new ApiError(data.message || 'Failed to fetch featured items', 400, data);
    return { items: data.items };
  },

  fetchItem: async (slug) => {
    const data = await fetchWithAuth(`/json/explore/${encodeURIComponent(slug)}`);
    if (data.error) throw new ApiError(data.message || 'Failed to fetch explore item', 400, data);
    return { item: data.item };
  },

  fetchCategories: async (mediaType = null) => {
    let url = '/json/explore/categories?withCounts=true';
    if (mediaType) url += `&mediaType=${encodeURIComponent(mediaType)}`;

    const data = await fetchWithAuth(url);
    if (data.error) throw new ApiError(data.message || 'Failed to fetch explore categories', 400, data);
    return { categories: data.categories };
  },

  fetchCategoriesDetailed: async (mediaType = null) => {
    let url = '/json/explore/categories?withFeatured=true&withCounts=true';
    if (mediaType) url += `&mediaType=${encodeURIComponent(mediaType)}`;

    const data = await fetchWithAuth(url);
    if (data.error) throw new ApiError(data.message || 'Failed to fetch explore categories', 400, data);
    return { categories: data.categories };
  },

  fetchTags: async (mediaType = null, limit = 30) => {
    let url = `/json/explore/tags?limit=${limit}`;
    if (mediaType) url += `&mediaType=${encodeURIComponent(mediaType)}`;

    const data = await fetchWithAuth(url);
    if (data.error) throw new ApiError(data.message || 'Failed to fetch explore tags', 400, data);
    return { tags: data.tags };
  },

  fetchModels: async (mediaType = null) => {
    let url = '/json/explore/models';
    if (mediaType) url += `?mediaType=${encodeURIComponent(mediaType)}`;

    const data = await fetchWithAuth(url);
    if (data.error) throw new ApiError(data.message || 'Failed to fetch explore models', 400, data);
    return { models: data.models };
  },

  fetchItemsByModel: async (modelId, limit = 12) => {
    if (!modelId) return { items: [] };
    const url = `/json/explore/by-model/${encodeURIComponent(modelId)}?limit=${limit}`;
    const data = await fetchWithAuth(url);
    if (data.error) throw new ApiError(data.message || 'Failed to fetch items for model', 400, data);
    return { items: data.items || [] };
  },

  cloneItem: async (slug) => {
    const data = await fetchWithAuth(`/json/explore/${encodeURIComponent(slug)}/clone`, { method: 'POST' });
    if (data.error) throw new ApiError(data.message || 'Failed to clone explore item', data.status || 400, data);
    return { clone: data.clone };
  },

  trackEvent: async (slug, event) => {
    const data = await fetchWithAuth(`/json/explore/${encodeURIComponent(slug)}/track`, {
      method: 'POST',
      body: { event },
    });
    return data;
  },

  favoriteItem: async (slug) => {
    const data = await fetchWithAuth(`/json/explore/${encodeURIComponent(slug)}/favorite`, { method: 'POST' });
    if (data.error) throw new ApiError(data.message || 'Failed to favorite explore item', data.status || 400, data);
    return !data.error;
  },

  unfavoriteItem: async (slug) => {
    const data = await fetchWithAuth(`/json/explore/${encodeURIComponent(slug)}/favorite`, { method: 'DELETE' });
    if (data.error) throw new ApiError(data.message || 'Failed to unfavorite explore item', data.status || 400, data);
    return !data.error;
  },

  // Mint a user-owned File pointing at an explore item's media so it can be
  // attached to the chat input (e.g. as the source image for image-to-video).
  createSourceFile: async (slug) => {
    const data = await fetchWithAuth(`/json/explore/${encodeURIComponent(slug)}/source-file`, { method: 'POST' });
    if (data.error) throw new ApiError(data.message || 'Failed to create source file', data.status || 400, data);
    return { file: data.file, imageToVideoModel: data.imageToVideoModel || null };
  },
};
